import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Camera, 
  Save, 
  ArrowLeft,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Upload
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm, useFileUpload } from '../../hooks/useApi';
import { uploadAPI } from '../../services/api';

const UserProfile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
  const [passwordUpdateSuccess, setPasswordUpdateSuccess] = useState(false);

  // Profile form
  const profileForm = useForm({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  }, {
    name: { required: true, minLength: 2 },
    email: { required: true, email: true },
    phone: { required: true, phone: true },
  });

  // Password form
  const passwordForm = useForm({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }, {
    currentPassword: { required: true },
    newPassword: { required: true, minLength: 6 },
    confirmPassword: { required: true, minLength: 6 },
  });

  // Avatar upload
  const { upload: uploadAvatar, uploading: avatarUploading } = useFileUpload(uploadAPI.uploadAvatar);

  // Handle profile update
  const handleProfileUpdate = async (formData) => {
    const result = await updateProfile(formData);
    if (result.success) {
      setProfileUpdateSuccess(true);
      setTimeout(() => setProfileUpdateSuccess(false), 3000);
    }
  };

  // Handle password change
  const handlePasswordChange = async (formData) => {
    if (formData.newPassword !== formData.confirmPassword) {
      return;
    }

    const result = await changePassword({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    });

    if (result.success) {
      setPasswordUpdateSuccess(true);
      passwordForm.reset();
      setTimeout(() => setPasswordUpdateSuccess(false), 3000);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const result = await uploadAvatar(file);
      if (result.success) {
        // Update user profile with new avatar
        await updateProfile({ avatar: result.data.avatarUrl });
      }
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substr(0, 2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      {/* Page Header */}
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <div className="flex items-center mb-8">
          <Link
            to="/dashboard"
            className="mr-6 p-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-2xl hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md transition-all duration-300 shadow-lg"
          >
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Profile Settings
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Manage your account settings and preferences
            </p>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-2 sm:space-x-4 md:space-x-8 px-4 sm:px-6 md:px-8 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 sm:py-6 px-2 sm:px-4 border-b-2 font-bold text-sm sm:text-base transition-all duration-300 touch-manipulation whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden sm:inline">Profile Information</span>
                <span className="sm:hidden">Profile</span>
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`py-4 sm:py-6 px-2 sm:px-4 border-b-2 font-bold text-sm sm:text-base transition-all duration-300 touch-manipulation whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="hidden sm:inline">Change Password</span>
                <span className="sm:hidden">Password</span>
              </button>
            </nav>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="p-4 sm:p-6 md:p-8">
              {profileUpdateSuccess && (
                <div className="mb-6 sm:mb-8 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800 p-4 sm:p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <p className="text-base sm:text-lg font-bold text-green-800 dark:text-green-400">
                        Profile updated successfully!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => profileForm.handleSubmit(e, handleProfileUpdate)}>
                {/* Avatar Section */}
                <div className="mb-8 sm:mb-10">
                  <label className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-4 sm:mb-6">
                    Profile Picture
                  </label>
                  <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 md:space-x-8">
                    <div className="relative">
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt="Profile"
                          className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover shadow-xl border-4 border-white dark:border-gray-700"
                        />
                      ) : (
                        <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-xl border-4 border-white dark:border-gray-700">
                          <span className="text-xl sm:text-2xl font-bold text-white">
                            {getInitials(user?.name || 'User')}
                          </span>
                        </div>
                      )}
                      {avatarUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="cursor-pointer inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-2xl shadow-lg text-sm sm:text-base font-bold text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-white/10 backdrop-blur-md hover:bg-gray-50 dark:hover:bg-white/20 transition-all duration-300 hover:scale-105 touch-manipulation">
                        <Camera className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                        Change Photo
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                          disabled={avatarUploading}
                        />
                      </label>
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 font-semibold">
                        JPG, PNG up to 2MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <User className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={profileForm.values.name}
                        onChange={(e) => profileForm.setValue('name', e.target.value)}
                        onBlur={() => profileForm.setFieldTouched('name', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          profileForm.errors.name && profileForm.touched.name
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter your full name"
                      />
                    </div>
                    {profileForm.errors.name && profileForm.touched.name && (
                      <p className="mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {profileForm.errors.name}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={profileForm.values.email}
                        onChange={(e) => profileForm.setValue('email', e.target.value)}
                        onBlur={() => profileForm.setFieldTouched('email', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          profileForm.errors.email && profileForm.touched.email
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter your email"
                      />
                    </div>
                    {profileForm.errors.email && profileForm.touched.email && (
                      <p className="mt-2 text-xs sm:text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {profileForm.errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={profileForm.values.phone}
                        onChange={(e) => profileForm.setValue('phone', e.target.value)}
                        onBlur={() => profileForm.setFieldTouched('phone', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          profileForm.errors.phone && profileForm.touched.phone
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter your phone number"
                      />
                    </div>
                    {profileForm.errors.phone && profileForm.touched.phone && (
                      <p className="mt-2 text-xs sm:text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {profileForm.errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-8 sm:mt-10 flex justify-end">
                  <button
                    type="submit"
                    disabled={profileForm.isSubmitting}
                    className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-glow disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2 sm:space-x-3 touch-manipulation"
                  >
                    {profileForm.isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span className="text-sm sm:text-base">Updating...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base">Update Profile</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="p-4 sm:p-6 md:p-8">
              {passwordUpdateSuccess && (
                <div className="mb-6 sm:mb-8 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800 p-4 sm:p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="ml-3 sm:ml-4">
                      <p className="text-base sm:text-lg font-bold text-green-800 dark:text-green-400">
                        Password changed successfully!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => passwordForm.handleSubmit(e, handlePasswordChange)}>
                <div className="space-y-6 sm:space-y-8">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      Current Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.values.currentPassword}
                        onChange={(e) => passwordForm.setValue('currentPassword', e.target.value)}
                        onBlur={() => passwordForm.setFieldTouched('currentPassword', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          passwordForm.errors.currentPassword && passwordForm.touched.currentPassword
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 sm:pr-3 sm:pr-4 flex items-center hover:scale-110 touch-manipulation transition-transform duration-200 touch-manipulation"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.errors.currentPassword && passwordForm.touched.currentPassword && (
                      <p className="mt-2 text-xs sm:text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {passwordForm.errors.currentPassword}
                      </p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      New Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.values.newPassword}
                        onChange={(e) => passwordForm.setValue('newPassword', e.target.value)}
                        onBlur={() => passwordForm.setFieldTouched('newPassword', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          passwordForm.errors.newPassword && passwordForm.touched.newPassword
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center hover:scale-110 touch-manipulation transition-transform duration-200"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.errors.newPassword && passwordForm.touched.newPassword && (
                      <p className="mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {passwordForm.errors.newPassword}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.values.confirmPassword}
                        onChange={(e) => passwordForm.setValue('confirmPassword', e.target.value)}
                        onBlur={() => passwordForm.setFieldTouched('confirmPassword', true)}
                        className={`block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 text-sm sm:text-base border-2 rounded-xl sm:rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-white/10 backdrop-blur-md dark:border-gray-600 dark:text-white transition-all duration-300 ${
                          passwordForm.errors.confirmPassword && passwordForm.touched.confirmPassword
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center hover:scale-110 touch-manipulation transition-transform duration-200"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.errors.confirmPassword && passwordForm.touched.confirmPassword && (
                      <p className="mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        {passwordForm.errors.confirmPassword}
                      </p>
                    )}
                    {passwordForm.values.newPassword && passwordForm.values.confirmPassword && 
                     passwordForm.values.newPassword !== passwordForm.values.confirmPassword && (
                      <p className="mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400 font-semibold">
                        Passwords do not match
                      </p>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-8 sm:mt-10 flex justify-end">
                  <button
                    type="submit"
                    disabled={passwordForm.isSubmitting || passwordForm.values.newPassword !== passwordForm.values.confirmPassword}
                    className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-glow disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2 sm:space-x-3 touch-manipulation"
                  >
                    {passwordForm.isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span className="text-sm sm:text-base">Changing...</span>
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base">Change Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
